import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ScrollArea } from "@/components/ui/scroll-area";
import { toast } from "sonner";
import { format, parse } from "date-fns";
import { 
  Settings, LogOut, QrCode, Calendar as CalendarIcon, 
  Utensils, BellRing, Filter, Trash2
} from "lucide-react";
import { cn } from "@/lib/utils";
import { getAllReservations, adminDeleteReservation } from "@/lib/api";

export default function AdminReservations() {
  const navigate = useNavigate();
  const [reservations, setReservations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(new Date());

  useEffect(() => {
    // Check auth
    if (sessionStorage.getItem("adminAuth") !== "true") {
      navigate("/admin");
      return;
    }

    loadReservations();
  }, [navigate, selectedDate]);

  const loadReservations = async () => {
    try {
      setLoading(true);
      const params = {};
      if (selectedDate) {
        const dateStr = format(selectedDate, "yyyy-MM-dd");
        params.start_date = dateStr;
        params.end_date = dateStr;
      }
      
      const res = await getAllReservations(params);
      setReservations(res.data);
    } catch (error) {
      console.error("Error loading reservations:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteReservation = async (reservationId) => {
    if (!window.confirm("Are you sure you want to delete this reservation?")) {
      return;
    }
    
    try {
      await adminDeleteReservation(reservationId);
      toast.success("Reservation deleted");
      loadReservations();
    } catch (error) {
      console.error("Error deleting reservation:", error);
      toast.error(error.response?.data?.detail || "Failed to delete reservation");
    }
  };

  const handleLogout = () => {
    sessionStorage.removeItem("adminAuth");
    navigate("/admin");
  };

  const goToToday = () => {
    setSelectedDate(new Date());
  };

  const isToday = selectedDate && format(selectedDate, "yyyy-MM-dd") === format(new Date(), "yyyy-MM-dd");

  const totalRevenue = reservations.reduce((sum, r) => sum + r.price, 0);
  const totalGuests = reservations.reduce((sum, r) => sum + r.guest_count, 0);

  return (
    <div className="min-h-screen bg-background" data-testid="admin-reservations">
      {/* Header */}
      <header className="bg-card border-b border-border sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-primary/20 rounded-full flex items-center justify-center">
                <Settings className="w-5 h-5 text-primary" />
              </div>
              <div>
                <h1 className="font-display text-xl font-bold">All Reservations</h1>
                <p className="text-sm text-muted-foreground">La Olla</p>
              </div>
            </div>
            <Button
              variant="ghost"
              onClick={handleLogout}
              data-testid="logout-btn"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </header>

      {/* Navigation */}
      <div className="bg-card/50 border-b border-border">
        <div className="max-w-7xl mx-auto px-4">
          <nav className="flex gap-1 overflow-x-auto py-2">
            <Link to="/admin/dashboard">
              <Button variant="ghost" data-testid="nav-dashboard">
                <Utensils className="w-4 h-4 mr-2" />
                Dashboard
              </Button>
            </Link>
            <Link to="/admin/reservations">
              <Button variant="ghost" className="bg-primary/10 text-primary" data-testid="nav-reservations">
                <CalendarIcon className="w-4 h-4 mr-2" />
                Reservations
              </Button>
            </Link>
            <Link to="/admin/qr-codes">
              <Button variant="ghost" data-testid="nav-qr-codes">
                <QrCode className="w-4 h-4 mr-2" />
                QR Codes
              </Button>
            </Link>
          </nav>
        </div>
      </div>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 py-6">
        {/* Date Filter */}
        <Card className="bg-card border-border mb-6">
          <CardHeader className="pb-2">
            <CardTitle className="font-display text-lg flex items-center gap-2">
              <Filter className="w-4 h-4" />
              Filter by Date
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap items-center gap-4">
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      "justify-start text-left font-normal bg-background/50",
                      !selectedDate && "text-muted-foreground"
                    )}
                    data-testid="date-filter-btn"
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {selectedDate ? format(selectedDate, "MMMM d, yyyy") : "Select date"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0 bg-card border-border">
                  <Calendar
                    mode="single"
                    selected={selectedDate}
                    onSelect={setSelectedDate}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>

              {!isToday && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={goToToday}
                  data-testid="today-btn"
                >
                  <CalendarIcon className="w-4 h-4 mr-1" />
                  Today
                </Button>
              )}
              
              {isToday && (
                <Badge variant="outline" className="text-primary border-primary">
                  Showing Today
                </Badge>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Summary */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <Card className="bg-card border-border">
            <CardContent className="pt-4">
              <p className="text-sm text-muted-foreground">Total Reservations</p>
              <p className="text-2xl font-bold" data-testid="total-reservations">{reservations.length}</p>
            </CardContent>
          </Card>
          <Card className="bg-card border-border">
            <CardContent className="pt-4">
              <p className="text-sm text-muted-foreground">Total Guests</p>
              <p className="text-2xl font-bold" data-testid="total-guests-summary">{totalGuests}</p>
            </CardContent>
          </Card>
          <Card className="bg-card border-border">
            <CardContent className="pt-4">
              <p className="text-sm text-muted-foreground">Total Revenue</p>
              <p className="text-2xl font-bold text-primary" data-testid="total-revenue">${totalRevenue.toFixed(2)}</p>
            </CardContent>
          </Card>
        </div>

        {/* Reservations Table */}
        <Card className="bg-card border-border">
          <CardContent className="p-0">
            <ScrollArea className="h-[500px]">
              <Table>
                <TableHeader>
                  <TableRow className="border-border hover:bg-transparent">
                    <TableHead>Date</TableHead>
                    <TableHead>Meal</TableHead>
                    <TableHead>Hotel</TableHead>
                    <TableHead>Cabin</TableHead>
                    <TableHead>Guest</TableHead>
                    <TableHead>Count</TableHead>
                    <TableHead>Price</TableHead>
                    <TableHead>Options</TableHead>
                    <TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={9} className="text-center py-8 text-muted-foreground">
                        Loading...
                      </TableCell>
                    </TableRow>
                  ) : reservations.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={9} className="text-center py-8 text-muted-foreground">
                        No reservations found
                      </TableCell>
                    </TableRow>
                  ) : (
                    reservations.map((res) => (
                      <TableRow key={res.id} className="border-border" data-testid={`res-row-${res.id}`}>
                        <TableCell>
                          {format(parse(res.date, "yyyy-MM-dd", new Date()), "MMM d")}
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline">{res.meal_name}</Badge>
                        </TableCell>
                        <TableCell className="font-medium">{res.hotel_name}</TableCell>
                        <TableCell>{res.cabin_name}</TableCell>
                        <TableCell>
                          <div>
                            <p>{res.guest_name}</p>
                            {res.room_service && (
                              <div className="flex items-center gap-1 text-warning text-sm font-medium mt-1">
                                <BellRing className="w-3 h-3" />
                                Room Service
                              </div>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>{res.guest_count}</TableCell>
                        <TableCell className="text-primary font-medium">
                          ${res.price.toFixed(2)}
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            {res.dietary_requirements && Object.keys(res.dietary_requirements).length > 0 && 
                              Object.values(res.dietary_requirements).some(v => v > 0) && (
                              <Badge variant="destructive" className="text-xs">
                                Diet
                              </Badge>
                            )}
                            {res.special_time_request && (
                              <Badge variant="outline" className="text-xs border-info text-info">
                                Time Req
                              </Badge>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleDeleteReservation(res.id)}
                            className="text-destructive hover:bg-destructive/10"
                            data-testid={`delete-res-${res.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </ScrollArea>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
